import React, { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { TermsContext } from "../../../utils/Context";
import { MULTI, SINGLE, Selector } from "../../Selectors/Selector";
import { sortTermsAscending } from "../utils";

const NONE_TERM_ID = "__none__";

export const TermSelect = ({
  selectHandler,
  selectedAcademicYears,
  noFutureTerms = false,
  noPastTerms = false,
  noDefaultTerm = false,
  clear,
  selectedTerms,
  isSingle,
  label,
  id,
  size,
  filteredTermIds,
  allowNone = false,
  width,
}) => {
  const { t } = useTranslation();
  const { insightsTerms } = useContext(TermsContext);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let insightsTermsList = [];

    if (selectedAcademicYears?.length > 0) {
      selectedAcademicYears.forEach((year) => {
        insightsTermsList = insightsTermsList.concat(year.insights_terms);
      });
    } else {
      insightsTermsList = insightsTerms.filter(
        (term) => !!term.canvas_terms && term.canvas_terms.length > 0
      );
    }

    const currentDate = new Date();

    if (noFutureTerms) {
      insightsTermsList = insightsTermsList.filter((term) => {
        const sortedCanvasTerms = sortTermsAscending(term.canvas_terms);
        // Finds the earliest canvas term with a valid start date
        const earliestCanvasTerm = sortedCanvasTerms.find(
          (term) => !!term.start_at
        );

        if (!earliestCanvasTerm) return false;

        const itemDate = new Date(earliestCanvasTerm.start_at);
        return itemDate <= currentDate;
      });
    }
    if (noPastTerms) {
      insightsTermsList = insightsTermsList.filter((term) => {
        const now = new Date();
        return (
          (new Date(term.start_at) <= now && new Date(term.end_at) >= now) ||
          new Date(term.start_at) >= now
        );
      });
    }
    if (filteredTermIds && filteredTermIds.length > 0) {
      insightsTermsList = insightsTermsList.filter((term) =>
        filteredTermIds.includes(term.id)
      );
    }
    if (noDefaultTerm) {
      insightsTermsList = insightsTermsList.filter(
        (term) => term.start_at !== "default"
      );
    }

    const sortedTerms = insightsTermsList
      .sort((a, b) => {
        const aIsDefault = a.start_at === "default";
        const bIsDefault = b.start_at === "default";

        if (aIsDefault && !bIsDefault) return -1;

        if (!aIsDefault && bIsDefault) return 1;

        return new Date(a.start_at) - new Date(b.start_at);
      })
      .map((term) => ({ ...term, label: term.name }));

    if (allowNone) {
      sortedTerms.unshift({ id: NONE_TERM_ID, label: t("global.none") });
    }

    setOptions(sortedTerms);
  }, [
    insightsTerms,
    selectedAcademicYears,
    noFutureTerms,
    noPastTerms,
    noDefaultTerm,
    filteredTermIds,
    allowNone,
  ]);

  const handleSelect = (idOrIds) => {
    if (Array.isArray(idOrIds)) {
      // MULTI: filter out NONE_TERM_ID if it somehow appears
      selectHandler(idOrIds.filter((id) => id !== NONE_TERM_ID));
    } else {
      selectHandler(idOrIds === NONE_TERM_ID ? null : idOrIds);
    }
  };

  return (
    <Selector
      type={isSingle ? SINGLE : MULTI}
      id={id || "term-select"}
      label={label}
      options={options}
      selectHandler={handleSelect}
      clear={clear}
      size={size}
      width={width}
      selectedOptions={!isSingle ? selectedTerms : null}
      selectedItemId={isSingle ? selectedTerms : null}
    />
  );
};
